import * as React from 'react'
// context
import { LinkingModeContext } from 'containers/Plan/Edit/layers/API/Linking'
import { ObjectivesListAPIContext } from 'containers/ObjectivesList/layers/API'
// constants
import { ENTITIES } from 'constants/index'
// types
import { ILinkingItem } from 'types/graphql'
// utils
import pipe from 'lodash/fp/pipe'
import filter from 'lodash/fp/filter'
import { safeGetOr } from 'utils/fp'

type Props = React.PropsWithChildren<unknown>

export declare namespace NObjectivesListLinkingBL {
  interface IContext {
    handleLinkObjective: (node: any) => () => ILinkingItem
    handleLinkAction: (node: any) => () => ILinkingItem
    handleLinkKPI: (node: any) => () => ILinkingItem
    handleUnlinkPlanItem: (linkingItems: ILinkingItem & any) => () => void
  }
}

export const ObjectivesListLinkingBLContext = React.createContext<NObjectivesListLinkingBL.IContext>({} as any)

const ObjectivesListLinkingBL: React.FC<Props> = ({ children }) => {
  // context
  const { linkingSource, refetchQuery } = React.useContext(LinkingModeContext)
  const { linkPlanItem, unlinkPlanItem } = React.useContext(ObjectivesListAPIContext)

  // methods
  const generateLinkingItemCreateInput = (destination, parent) => {
    if ((linkingSource.entityType || parent.entityType) === ENTITIES.PLAN_OBJECTIVE) {
      return { ...destination, sourcePlanObjectiveId: linkingSource.id || parent.entityId }
    }

    if ((linkingSource.entityType || parent.entityType) === ENTITIES.PLAN_ACTION) {
      return { ...destination, sourcePlanActionId: linkingSource.id || parent.entityId }
    }

    if ((linkingSource.entityType || parent.entityType) === ENTITIES.PLAN_KPI) {
      return { ...destination, sourcePlanKPIId: linkingSource.id || parent.entityId }
    }
  }

  const handleLinkObjective = (node) => () => {
    const linkingItemCreateInput = generateLinkingItemCreateInput(
      { destinationPlanObjectiveId: node.entityId },
      node.parent
    )

    return linkPlanItem({ linkingItemCreateInput })
  }

  const handleLinkAction = (node) => () => {
    const linkingItemCreateInput = generateLinkingItemCreateInput(
      { destinationPlanActionId: node.entityId },
      node.parent
    )

    return linkPlanItem({ linkingItemCreateInput })
  }

  const handleLinkKPI = (node) => () => {
    const linkingItemCreateInput = generateLinkingItemCreateInput({ destinationPlanKPIId: node.entityId }, node.parent)

    return linkPlanItem({ linkingItemCreateInput })
  }

  const handleUnlinkPlanItem = (linkingItems) => () => {
    let planItemId

    if (linkingSource.entityType === ENTITIES.PLAN_OBJECTIVE) {
      planItemId = pipe(
        filter(({ sourcePlanObjectiveId }) => sourcePlanObjectiveId === linkingSource.id),
        safeGetOr<any, any, any>(null, '[0].id')
      )(linkingItems.destinationLinkingItems)
    }
    if (linkingSource.entityType === ENTITIES.PLAN_ACTION) {
      planItemId = pipe(
        filter(({ sourcePlanActionId }) => sourcePlanActionId === linkingSource.id),
        safeGetOr<any, any, any>(null, '[0].id')
      )(linkingItems.destinationLinkingItems)
    }
    if (linkingSource.entityType === ENTITIES.PLAN_KPI) {
      planItemId = pipe(
        filter(({ sourcePlanKPIId }) => sourcePlanKPIId === linkingSource.id),
        safeGetOr<any, any, any>(null, '[0].id')
      )(linkingItems.destinationLinkingItems)
    }

    unlinkPlanItem(planItemId, refetchQuery)
  }

  const value = { handleLinkObjective, handleLinkAction, handleLinkKPI, handleUnlinkPlanItem }

  return <ObjectivesListLinkingBLContext.Provider value={value}>{children}</ObjectivesListLinkingBLContext.Provider>
}

export default ObjectivesListLinkingBL
